<template>
  <div
    class="banner"
    :class="{ 'banner--filtered': applyImageFilter }"
    :style="{
      '--background-color': backgroundColor,
      '--filter-blur': `${imageFilterBlur / 10}px`,
    }"
  >
    <div
      v-if="background !== 'backgroundColor'"
      class="banner__background"
      :class="`banner__background--${orientation}`"
    >
      <iframe
        v-if="background === 'backgroundVideo' && !isMobile"
        :src="
          youtubeEmbed({
            url: linkVideo,
            autoplay: true,
            loop: true,
            mute: true,
          })
        "
        frameborder="0"
        allowfullscreen
        allow="autoplay"
      >
      </iframe>
      <img
        v-else
        :src="
          (isMobile && differentMobileBackgroundImage) ||
          background === 'backgroundVideo'
            ? imageMobile
            : image
        "
        :alt="
          (isMobile && differentMobileBackgroundImage && imageMobileAltText) ||
          background === 'backgroundVideo'
            ? imageMobileAltText
            : imageAltText
        "
      />
    </div>
    <div
      class="banner__content"
      :class="`banner__content--${orientation}`"
    >
      <header class="base-title-fontsize">
        <p
          v-if="topSubtitle"
          class="top-subtitle"
        >
          {{ topSubtitle }}
        </p>
        <component
          :is="titleTag"
          v-if="title"
          class="banner-title"
          :style="{
            fontWeight: titleWeight,
          }"
        >
          {{ title }}
        </component>
      </header>
      <div
        class="banner__cta"
        :class="`banner__cta--${orientation}`"
      >
        <EditorLink
          v-if="showButton(undefined, linkUrl)"
          :model="linkUrl"
          class="stretched-link base-content-fontsize"
        >
          <span
            v-if="buttonText"
            class="cta b-1"
            v-html="buttonText"
          ></span>
        </EditorLink>

        <p
          v-if="subtitle"
          class="base-content-fontsize"
        >
          {{ subtitle }}
        </p>
      </div>
      <slot></slot>
    </div>
  </div>
</template>

<script>
import showButton from '@/mixins/showButton';
import { youtubeEmbed } from '@/helpers/youtube';
import EditorLink from '../EditorLink.vue';

export default {
  name: 'BannerCard',
  components: {
    EditorLink,
  },
  mixins: [showButton],
  props: {
    applyImageFilter: {
      type: Boolean,
      default: false,
    },
    imageFilterBlur: {
      type: Number,
      default: 0,
    },
    imageFilterColor: {
      type: String,
      default: '#000000',
    },
    buttonText: {
      type: String,
      default: '',
    },
    linkUrl: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    background: {
      type: String,
      default: 'backgroundImage',
    },
    backgroundColor: {
      type: String,
      default: '#000000',
    },
    linkVideo: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    image: {
      type: String,
      default: '',
    },
    imageAltText: {
      type: String,
      default: '',
    },
    differentMobileBackgroundImage: {
      type: Boolean,
      default: false,
    },
    imageMobile: {
      type: String,
      default: '',
    },
    imageMobileAltText: {
      type: String,
      default: '',
    },
    textPosition: {
      type: String,
      default: 'Esquerda',
    },
    topSubtitle: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    titleTag: {
      type: String,
      default: 'h2',
    },
    titleWeight: {
      type: String,
      default: '300',
    },
    subtitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isMobile: window.innerWidth < 640,
    };
  },
  computed: {
    orientation() {
      switch (this.textPosition) {
        case 'Esquerda':
          return 'left';
        case 'Direita':
          return 'right';
        default:
          return 'center';
      }
    },
  },
  mounted() {
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
  },
  methods: {
    youtubeEmbed,
  },
};
</script>

<style lang="scss" scoped>
.banner {
  position: relative;
  z-index: 1;
  display: flex;
  background-color: var(--background-color, $lb-ice);

  &--filtered .banner__background::before {
    content: '';
    background: radial-gradient(
      circle at 40% 50%,
      rgba($lb-black, 0.8),
      rgba($lb-black, 0.5) 65%
    );
    backdrop-filter: blur(var(--filter-blur));
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  &__background {
    overflow: hidden;
    z-index: -1;

    &,
    > * {
      position: absolute;
      top: 50%;
      left: 50%;
      max-width: 100%;
      min-width: 100%;
      min-height: 100%;
      transform: translate(-50%, -50%);
    }

    img {
      max-height: 100%;

      @include media-breakpoint-up(sm) {
        max-height: unset;
        max-width: 100%;
      }
    }

    iframe {
      min-width: 150%;
      min-height: 150%;
    }
  }

  &__content {
    margin: auto;
    width: var(--wrapper);
    max-width: 95%;
    padding: 56px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: $lb-white;

    @include media-breakpoint-up(sm) {
      &--right {
        align-items: flex-end;
        text-align: right;
      }

      &--left {
        align-items: flex-start;
        text-align: left;
      }

      > * {
        max-width: 60%;
      }
    }

    h1,
    h2 {
      word-break: break-word;
      font-weight: $lb-light;
      line-height: 1.1em;
      text-shadow: 5px 5px 18px rgba($lb-black, 0.5);
    }

    h1 {
      font-size: 8.125vw;

      @include media-breakpoint-up(sm) {
        font-size: 45px;
      }
    }

    h2 {
      font-size: 32px;

      @include media-breakpoint-up(sm) {
        font-size: 40px;
      }
    }

    > p {
      margin-bottom: 10px;
      font-size: $size-normal;
      text-shadow: 3px 3px 10px rgba($lb-black, 0.5);
    }
  }

  &__cta {
    display: flex;
    align-items: center;
    margin-top: 30px;
    justify-content: center;
    flex-direction: column-reverse;
    white-space: pre-line;

    @include media-breakpoint-up(sm) {
      flex-direction: row;
    }

    .cta {
      margin-top: 45px;
      padding: 0.93em 1.88em;

      @include media-breakpoint-up(sm) {
        margin-right: 30px;
        margin-top: 0;
      }

      &,
      &::before,
      &::after {
        background-color: var(--primary-color);
      }
    }

    p {
      line-height: 1.31em;
    }
  }
}
</style>
